import { useState } from 'react';
import { route } from '../../utils/route';
import { getCSRFToken } from '../../utils/csrf';
import type { ProductPack } from '../../types/packs';
import { PackItemsList } from '../packs/PackItemsList';

export interface SuggestedProduct {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  webp_image_url: string | null;
  has_image: boolean;
  format_id: number;
  format_name: string | null;
  price: number;
}

export type SuggestedItem =
  | { type: 'pack'; pack: ProductPack }
  | { type: 'product'; product: SuggestedProduct };

interface SuggestedItemsProps {
  items: SuggestedItem[];
  discountCode: string;
  discountPercent: number;
  isAuthenticated: boolean;
  isPerUser: boolean;
  currentUrl: string;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

async function applyDiscountCode(code: string): Promise<void> {
  const response = await fetch(route('api.discount.apply', { code }), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getCSRFToken(),
      Accept: 'application/json',
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) {
    throw new Error(data?.message || 'No se pudo aplicar el descuento.');
  }
}

async function addItemsToCart(
  items: { id: number; format_id: number; quantity: number; source?: string }[]
): Promise<void> {
  const response = await fetch(route('api.cart.add_item'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getCSRFToken(),
    },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    throw new Error('No se pudo agregar al carrito.');
  }
}

export function SuggestedItems({
  items,
  discountCode,
  discountPercent,
  isAuthenticated,
  isPerUser,
  currentUrl,
}: SuggestedItemsProps) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async (item: SuggestedItem) => {
    if (busyKey) return;

    if (isPerUser && !isAuthenticated) {
      const returnUrl = currentUrl + (currentUrl.includes('?') ? '&' : '?') + 'apply';
      window.location.href = `/login?returnTo=${encodeURIComponent(returnUrl)}`;
      return;
    }

    const key = item.type === 'pack' ? `pack:${item.pack.id}` : `product:${item.product.id}`;
    setBusyKey(key);
    setError(null);

    try {
      await applyDiscountCode(discountCode);

      if (item.type === 'pack') {
        const pack = item.pack;
        if (pack.has_choice_groups) {
          window.location.href = route('packs.show', { id: pack.slug || pack.id });
          return;
        }
        const cartItems = pack.items.map((i) => ({
          id: i.product_id,
          format_id: i.product_format_id,
          quantity: i.quantity,
          source: `discount:${discountCode}`,
        }));
        await addItemsToCart(cartItems);
      } else {
        const product = item.product;
        await addItemsToCart([
          {
            id: product.id,
            format_id: product.format_id,
            quantity: 1,
            source: `discount:${discountCode}`,
          },
        ]);
      }

      window.location.href = route('cart.review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo salió mal. Intentalo de nuevo.');
      setBusyKey(null);
    }
  };

  if (items.length === 0) return null;

  const single = items.length === 1 ? items[0] : null;

  return (
    <div className="flex w-full flex-col rounded-2xl bg-white p-5 shadow-lg lg:min-h-0 lg:flex-1 lg:p-6">
      {single ? (
        <SuggestedItemHero
          item={single}
          discountPercent={discountPercent}
          isBusy={busyKey !== null}
          onAdd={() => handleAdd(single)}
          itemBusyKey={busyKey}
        />
      ) : (
        <>
          <div className="mb-1 text-[15px] font-bold text-gray-900">Aprovechá esta selección</div>
          <p className="mb-4 text-[13px] text-gray-500">
            Agregalas con tu {Math.round(discountPercent)}% aplicado automáticamente
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {items.map((item) => (
              <SuggestedItemMini
                key={item.type === 'pack' ? `pack:${item.pack.id}` : `product:${item.product.id}`}
                item={item}
                discountPercent={discountPercent}
                isBusy={busyKey !== null}
                isThisBusy={
                  item.type === 'pack'
                    ? busyKey === `pack:${item.pack.id}`
                    : busyKey === `product:${item.product.id}`
                }
                onAdd={() => handleAdd(item)}
              />
            ))}
          </div>
        </>
      )}

      {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
    </div>
  );
}

function SuggestedItemHero({
  item,
  discountPercent,
  isBusy,
  onAdd,
  itemBusyKey,
}: {
  item: SuggestedItem;
  discountPercent: number;
  isBusy: boolean;
  onAdd: () => void;
  itemBusyKey: string | null;
}) {
  if (item.type === 'pack') {
    return (
      <PackHero
        pack={item.pack}
        discountPercent={discountPercent}
        isBusy={isBusy}
        isThisBusy={itemBusyKey === `pack:${item.pack.id}`}
        onAdd={onAdd}
      />
    );
  }
  return (
    <ProductHero
      product={item.product}
      discountPercent={discountPercent}
      isBusy={isBusy}
      isThisBusy={itemBusyKey === `product:${item.product.id}`}
      onAdd={onAdd}
    />
  );
}

function PackHero({
  pack,
  discountPercent,
  isBusy,
  isThisBusy,
  onAdd,
}: {
  pack: ProductPack;
  discountPercent: number;
  isBusy: boolean;
  isThisBusy: boolean;
  onAdd: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const discountedPrice = pack.calculated_price * (1 - discountPercent / 100);
  const savings = pack.calculated_price - discountedPrice;
  const buttonLabel = pack.has_choice_groups
    ? 'Elegí las opciones del pack'
    : isThisBusy
      ? 'Agregando...'
      : 'Agregá el pack con descuento';

  return (
    <div className="flex w-full flex-col lg:min-h-0 lg:flex-1">
      <div className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-green-700">
        <i className="fa fa-gift" /> Selección recomendada
      </div>

      <div className="overflow-hidden rounded-xl bg-gray-50 lg:min-h-0 lg:flex-1">
        {pack.image_url ? (
          <img
            src={pack.thumb_url || pack.image_url}
            alt={pack.title}
            className="aspect-[3/2] w-full object-contain lg:aspect-auto lg:h-full"
          />
        ) : (
          <div className="flex aspect-[3/2] w-full items-center justify-center text-6xl text-gray-300 lg:aspect-auto lg:h-full">
            <i className="fa fa-box" />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col">
        <h3 className="m-0 text-lg font-bold leading-snug text-gray-900">{pack.title}</h3>
        {pack.subtitle && <p className="mt-1 text-sm text-gray-500">{pack.subtitle}</p>}
        {pack.description && (
          <p className="mt-2 text-[13px] leading-relaxed text-gray-600">{pack.description}</p>
        )}

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-sm text-gray-400 line-through">{formatPrice(pack.calculated_price)}</span>
          <span className="text-2xl font-bold text-green-600">{formatPrice(discountedPrice)}</span>
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-semibold text-green-700">
            -{Math.round(discountPercent)}%
          </span>
        </div>
        <div className="text-[12px] font-medium text-green-700">Ahorrás {formatPrice(savings)}</div>
      </div>

      <PackItemsList
        items={pack.items}
        choiceGroups={pack.choice_groups}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      <button
        type="button"
        onClick={onAdd}
        disabled={isBusy}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
      >
        {!isThisBusy && <i className="fa fa-shopping-cart" />}
        {isThisBusy && <i className="fa fa-spinner fa-spin" />}
        {buttonLabel}
      </button>
    </div>
  );
}

function ProductHero({
  product,
  discountPercent,
  isBusy,
  isThisBusy,
  onAdd,
}: {
  product: SuggestedProduct;
  discountPercent: number;
  isBusy: boolean;
  isThisBusy: boolean;
  onAdd: () => void;
}) {
  const discountedPrice = product.price * (1 - discountPercent / 100);
  const savings = product.price - discountedPrice;

  return (
    <div className="flex w-full flex-col lg:min-h-0 lg:flex-1">
      <div className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-green-700">
        <i className="fa fa-tag" /> Producto recomendado
      </div>

      <div className="overflow-hidden rounded-xl bg-gray-50 lg:min-h-0 lg:flex-1">
        {product.has_image ? (
          <img
            src={product.webp_image_url || product.image_url || ''}
            alt={product.title}
            className="aspect-[3/2] w-full object-contain p-4 lg:aspect-auto lg:h-full"
          />
        ) : (
          <div className="flex aspect-[3/2] w-full items-center justify-center text-6xl text-gray-300 lg:aspect-auto lg:h-full">
            <i className="fa fa-leaf" />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col">
        <h3 className="m-0 text-lg font-bold leading-snug text-gray-900">{product.title}</h3>
        {product.format_name && (
          <p className="mt-1 text-sm text-gray-500">{product.format_name}</p>
        )}
        {product.description && (
          <p className="mt-2 text-[13px] leading-relaxed text-gray-600">{product.description}</p>
        )}

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-sm text-gray-400 line-through">{formatPrice(product.price)}</span>
          <span className="text-2xl font-bold text-green-600">{formatPrice(discountedPrice)}</span>
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-semibold text-green-700">
            -{Math.round(discountPercent)}%
          </span>
        </div>
        <div className="text-[12px] font-medium text-green-700">Ahorrás {formatPrice(savings)}</div>
      </div>

      <button
        type="button"
        onClick={onAdd}
        disabled={isBusy}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
      >
        {!isThisBusy && <i className="fa fa-shopping-cart" />}
        {isThisBusy && <i className="fa fa-spinner fa-spin" />}
        {isThisBusy ? 'Agregando...' : 'Agregalo con descuento'}
      </button>
    </div>
  );
}

function SuggestedItemMini({
  item,
  discountPercent,
  isBusy,
  isThisBusy,
  onAdd,
}: {
  item: SuggestedItem;
  discountPercent: number;
  isBusy: boolean;
  isThisBusy: boolean;
  onAdd: () => void;
}) {
  if (item.type === 'pack') {
    const pack = item.pack;
    const discountedPrice = pack.calculated_price * (1 - discountPercent / 100);
    return (
      <div className="flex h-full flex-col rounded-xl border border-gray-100 bg-gray-50/50 p-3">
        <div className="mb-3 flex gap-3">
          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-50">
            {pack.image_url ? (
              <img src={pack.thumb_url || pack.image_url} alt={pack.title} className="h-full w-full object-contain" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-2xl text-gray-300">
                <i className="fa fa-box" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-green-700">Pack</div>
            <div className="line-clamp-2 text-[13px] font-semibold leading-tight text-gray-800">{pack.title}</div>
            <div className="mt-1 text-[11px] text-gray-400 line-through">{formatPrice(pack.calculated_price)}</div>
            <div className="text-sm font-bold text-green-600">{formatPrice(discountedPrice)}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={isBusy}
          className="mt-auto flex items-center justify-center gap-2 rounded-lg bg-[#1EAA25] px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
        >
          {isThisBusy ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-shopping-cart" />}
          {pack.has_choice_groups
            ? 'Elegir opciones'
            : isThisBusy
              ? 'Agregando...'
              : 'Agregá el pack'}
        </button>
      </div>
    );
  }

  const product = item.product;
  const discountedPrice = product.price * (1 - discountPercent / 100);
  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-100 bg-gray-50/50 p-3">
      <div className="mb-3 flex gap-3">
        <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
          {product.has_image ? (
            <img src={product.webp_image_url || product.image_url || ''} alt={product.title} className="aspect-square w-full object-contain" />
          ) : (
            <i className="fa fa-leaf text-2xl text-gray-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-green-700">Producto</div>
          <div className="line-clamp-2 text-[13px] font-semibold leading-tight text-gray-800">{product.title}</div>
          {product.format_name && (
            <div className="text-[11px] text-gray-500">{product.format_name}</div>
          )}
          <div className="mt-1 text-[11px] text-gray-400 line-through">{formatPrice(product.price)}</div>
          <div className="text-sm font-bold text-green-600">{formatPrice(discountedPrice)}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={isBusy}
        className="mt-auto flex items-center justify-center gap-2 rounded-lg bg-[#1EAA25] px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
      >
        {isThisBusy ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-shopping-cart" />}
        {isThisBusy ? 'Agregando...' : 'Agregalo'}
      </button>
    </div>
  );
}
